import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  SkeletonBodyText,
  EmptyState,
  Box,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { Doctor } from '@/types';

function getStatusBadge(status: string) {
  const tone = status === 'active' ? 'success' : status === 'inactive' ? 'critical' : undefined;
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <Badge tone={tone}>{label}</Badge>;
}

export default function ClinicDoctors() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clinic-doctors'],
    queryFn: () => clinicPortalApi.getDoctors(),
  });

  const rows = (data || []).map((doctor: Doctor) => [
    `${doctor.firstName} ${doctor.lastName}`,
    doctor.email || 'N/A',
    doctor.phone || 'N/A',
    getStatusBadge(doctor.status),
  ]);

  if (error) {
    return (
      <Page title="Doctors">
        <Card>
          <EmptyState
            heading="Error loading doctors"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Doctors">
      <Card padding="0">
        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={6} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No doctors found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Doctors associated with your clinic will appear here.</p>
            </EmptyState>
          </Box>
        ) : (
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text']}
            headings={['Name', 'Email', 'Phone', 'Status']}
            rows={rows}
          />
        )}
      </Card>
    </Page>
  );
}
